import { useFormatting } from "@msys/ui";
import { Button, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { FilterChip } from "../../commons/filters/FilterChip.js";
import { ChangeLogDateRangeDropdown } from "./ChangeLogDateRangeDropdown.js";
import { ChangeLogFilterDropdown } from "./ChangeLogFilterDropdown.js";
import { useChangeLogLabels } from "./changeLogLabels.js";
import {
  type ChangeLogEntityStrategy,
  type ChangeLogFacetsData,
  type ChangeLogFilterState,
  EMPTY_CHANGE_LOG_FILTERS,
} from "./changeLogTypes.js";

type Option = { value: string; label: string };

interface Props {
  facets: ChangeLogFacetsData | null;
  filters: ChangeLogFilterState;
  setFilters: React.Dispatch<React.SetStateAction<ChangeLogFilterState>>;
  entityStrategy: ChangeLogEntityStrategy;
}

/**
 * Templates-style filter bar (design D8): an occurred-date range (**From** / **To**)
 * followed by a row of `ButtonSelectMultiple` dropdown buttons — **user** and **item** —
 * each opening a searchable, multi-select popover (the same primitive the templates list
 * uses, e.g. `TemplateFilterFields`). The control order mirrors the change-log table's
 * columns (D7): Time → User → Items, so the date range leads and the item dropdown is
 * last. The user dropdown is a per-identity actor picker (D9) — every acting identity
 * (`user:` / `system:` / `integration:`) is an ordinary option keyed by its `actorId`, no
 * separate "System" sentinel. There is no provenance/"Operation" (trigger) filter (design
 * D4): provenance is gone from the model, so the bar offers no trigger option and no
 * synthetic "Edited" plain-edit option. The occurred-time range is a **"Time" dropdown**
 * (`ChangeLogDateRangeDropdown`) matching the other filter buttons — its popover holds
 * From / To `DateTimePicker`s whose chosen moment is the exact inclusive bound, serialised
 * to `occurredFrom` / `occurredTo` (D5). Selections map to the `filters` state (OR within /
 * AND across dimensions) → re-query, and surface as removable `FilterChip`s (in the same
 * column order) with a "Clear all" reset. Item labels come from the aggregate's
 * `ChangeLogEntityStrategy`.
 */
export function ChangeLogFilterBar({
  facets,
  filters,
  setFilters,
  entityStrategy,
}: Props) {
  const { t } = useTranslate(["ChangeLog", "Global"]);
  const { getFormattedDateTime } = useFormatting();
  const labels = useChangeLogLabels();

  const searchLabel = t("Search", { ns: "Global" });
  const noOptionsLabel = t("There are no items to display", { ns: "Global" });
  const dropdownProps = { searchLabel, noOptionsLabel } as const;

  const itemOptions = React.useMemo<Option[]>(
    () =>
      (facets?.entities ?? []).map(e => ({
        value: e.entityId,
        label: entityStrategy.entityLabel(e),
      })),
    [facets?.entities, entityStrategy]
  );

  // One option per acting identity (`actorId`), including named system/integration
  // identities (they carry a static-registry `label` and are ordinary options now —
  // no separate "System" sentinel). Labelled through the shared actor-label rule
  // (target-naming D6) — a deleted user's actor (`label` null, `userId` set) reads the
  // localized "Deleted user" rather than leaking its raw `user:<uuid>` id; the option's
  // `value` stays the exact `actorId`, so it is still individually selectable/filterable.
  const actorOptions = React.useMemo<Option[]>(
    () =>
      (facets?.actors ?? []).map(a => ({
        value: a.actorId,
        label: labels.actorLabel(a),
      })),
    [facets?.actors, labels]
  );

  // A selected value can be absent from `options` only while the facets are still
  // loading (the facet queries are unfiltered and log entries are immutable, so a
  // loaded facet list always contains every selectable value). Fall back to a neutral
  // placeholder — never the raw value, which would leak a `user:<uuid>`/entity id.
  const labelOf = (options: Option[], value: string): string =>
    options.find(o => o.value === value)?.label ?? "…";

  const hasAnyFilter =
    filters.entityIds.length > 0 ||
    filters.actors.length > 0 ||
    filters.occurredFrom !== null ||
    filters.occurredTo !== null;

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {/* Order mirrors the table columns (D7): Time → User → Items. */}
        <ChangeLogDateRangeDropdown
          label={t("Time", { ns: "Global" })}
          fromLabel={t("From", { ns: "Global" })}
          toLabel={t("To", { ns: "Global" })}
          from={filters.occurredFrom}
          to={filters.occurredTo}
          onChange={({ from, to }) =>
            setFilters(f => ({ ...f, occurredFrom: from, occurredTo: to }))
          }
        />
        <ChangeLogFilterDropdown
          label={t("User", { ns: "Global" })}
          {...dropdownProps}
          options={actorOptions}
          value={filters.actors}
          onChange={vals => setFilters(f => ({ ...f, actors: vals }))}
        />
        <ChangeLogFilterDropdown
          label={t("Item", { ns: "Global" })}
          {...dropdownProps}
          options={itemOptions}
          value={filters.entityIds}
          onChange={ids => setFilters(f => ({ ...f, entityIds: ids }))}
          width={360}
        />
      </Stack>
      {hasAnyFilter && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          {/* Chip order mirrors the table columns (D7): Time → User → Items. */}
          {filters.occurredFrom !== null && (
            <FilterChip
              key="occurred-from"
              label={t("From", { ns: "Global" })}
              value={getFormattedDateTime(filters.occurredFrom)}
              onDelete={() => setFilters(f => ({ ...f, occurredFrom: null }))}
            />
          )}
          {filters.occurredTo !== null && (
            <FilterChip
              key="occurred-to"
              label={t("To", { ns: "Global" })}
              value={getFormattedDateTime(filters.occurredTo)}
              onDelete={() => setFilters(f => ({ ...f, occurredTo: null }))}
            />
          )}
          {filters.actors.map(v => (
            <FilterChip
              key={`actor-${v}`}
              label={t("User", { ns: "Global" })}
              value={labelOf(actorOptions, v)}
              onDelete={() =>
                setFilters(f => ({
                  ...f,
                  actors: f.actors.filter(a => a !== v),
                }))
              }
            />
          ))}
          {filters.entityIds.map(v => (
            <FilterChip
              key={`item-${v}`}
              label={t("Item", { ns: "Global" })}
              value={labelOf(itemOptions, v)}
              onDelete={() =>
                setFilters(f => ({
                  ...f,
                  entityIds: f.entityIds.filter(id => id !== v),
                }))
              }
            />
          ))}
          <Button
            size="small"
            onClick={() => setFilters(EMPTY_CHANGE_LOG_FILTERS)}
          >
            {t("Clear all", { ns: "Global" })}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
